import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import config from "../../config";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// SvgIcon — matches the same pattern used in useMapIcons.fetchAndColorizeSVG
// Fetches raw SVG from S3, replaces ${color} placeholder, renders inline
// ---------------------------------------------------------------------------
const SvgIcon = ({ url, fillColor = "green", size = 32, alt = "Icon" }) => {
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;

    const fetchSvg = async () => {
      try {
        const response = await fetch(url);
        const svgText = await response.text();
        if (cancelled) return;

        const colorized = svgText
          .replace(/\$\{color\}/g, fillColor)
          .replace(/currentColor/gi, fillColor);

        // Convert to base64 data URL — img tag is fully CSS-isolated
        const encoded = btoa(unescape(encodeURIComponent(colorized)));
        setImgSrc(`data:image/svg+xml;base64,${encoded}`);
      } catch (err) {
        console.error("Failed to fetch SVG icon:", err);
        setImgSrc(url); // fallback to original URL
      }
    };

    fetchSvg();
    return () => {
      cancelled = true;
    };
  }, [url, fillColor, size]);

  return (
    <Box
      component="img"
      src={imgSrc || url}
      alt={alt}
      sx={{
        width: size,
        height: size,
        objectFit: "contain",
        flexShrink: 0,
        display: "block",
      }}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// IconSelector
// ---------------------------------------------------------------------------
const IconSelector = ({
  iconType,
  label,
  value,
  onChange,
  disabled,
  organizationId,
}) => {
  const { t } = useTranslation();
  const [availableIcons, setAvailableIcons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAvailableIcons = async () => {
      if (!iconType) return;
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${config.baseURL}/icon/available/${iconType}`,
          { withCredentials: true },
        );
        const icons = response.data.icons || [];
        setAvailableIcons(icons);
      } catch (error) {
        console.error(`Error fetching ${iconType} icons:`, error);
        setError("Failed to load icons");
        setAvailableIcons([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAvailableIcons();
  }, [iconType]);

  const handleChange = (event) => {
    onChange({
      target: {
        name: iconType,
        value: event.target.value,
      },
    });
  };

  const getIconUrl = (iconPath) => {
    if (!iconPath) return "";
    return `${config.baseURL}/icon/file/${iconPath}`;
  };

  const getDisplayLabel = (icon, index) => {
    const iconLabelKeyByType = {
      vehicleIcon: "Vehicle Icon",
      stopOriginIcon: "Stop Origin Icon",
      stopDestinationIcon: "Stop Destination Icon",
      intermediateStopIcon: "Intermediate Stop Icon",
    };
    const baseLabel = iconLabelKeyByType[iconType] || "Icon";
    return `${t(baseLabel)} ${index + 1}`;
  };

  // Mirror the same color logic used in useMapIcons
  // vehicle.vehicle_color || vehicle.color || "green"
  const getFillColor = () => {
    switch (iconType) {
      case "vehicle":
        return "green";
      case "stop":
        return "blue";
      default:
        return "green";
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: "48px",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          backgroundColor: "#F5F5F5",
          px: 2,
        }}
      >
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          {t("Loading icons...")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "48px",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          px: 2,
        }}
      >
        <Typography variant="body2" color="error">
          {t(error)}
        </Typography>
      </Box>
    );
  }

  const fillColor = getFillColor();

  return (
    <FormControl fullWidth disabled={disabled}>
      <Select
        value={value || ""}
        onChange={handleChange}
        displayEmpty
        sx={{
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#A5DCF3",
          },
          "& .MuiSelect-select": {
            display: "flex !important",
            alignItems: "center !important",
            paddingTop: "8px !important",
            paddingBottom: "8px !important",
          },
        }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography variant="body2" color="text.secondary">
                {t("Select an icon")}
              </Typography>
            );
          }

          const selectedIcon = availableIcons.find((i) => i.path === selected);
          const selectedIndex = availableIcons.findIndex(
            (i) => i.path === selected,
          );

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <SvgIcon
                url={getIconUrl(selected)}
                fillColor={fillColor}
                size={32}
                alt={selectedIcon?.name || "Icon"}
              />
              <Typography variant="body2">
                {selectedIcon
                  ? getDisplayLabel(selectedIcon, selectedIndex)
                  : t("Selected Icon")}
              </Typography>
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
              "& .MuiMenuItem-root": { padding: "6px 12px" },
            },
          },
        }}
      >
        {availableIcons.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t("No icons available")}
            </Typography>
          </MenuItem>
        ) : (
          availableIcons.map((icon, index) => (
            <MenuItem
              key={icon.path}
              value={icon.path}
              sx={{
                "&:hover": { backgroundColor: "#F5F5F5" },
                "&:hover *": { color: "inherit" }, // ← prevent color change on all children
                color: "rgba(0, 0, 0, 0.87)", // ← lock the base color
                "&.Mui-focusVisible": { backgroundColor: "#F5F5F5" },
                "&.Mui-selected": { backgroundColor: "#EEF7FF" },
                "&.Mui-selected:hover": { backgroundColor: "#EEF7FF" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <SvgIcon
                  url={getIconUrl(icon.path)}
                  fillColor={fillColor}
                  size={32}
                  alt={icon.name || getDisplayLabel(icon, index)}
                />
                <Typography variant="body2">
                  {getDisplayLabel(icon, index)}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export default IconSelector;
